/**
 * LEVANTAMIENTO DE ILUMINACIÓN — la guía y la lista de espacios.
 * =============================================================================
 *
 * Quinto rubro, y el primero que **no lleva plano**. No es un descuido: es que aquí la
 * geometría que importa no es dónde está cada cosa sino **cuánto mide cada cuarto**, y una
 * lista de espacios con sus medidas dice eso mejor que un dibujo. Forzar un lienzo habría
 * sido copiar la forma de los otros rubros sin su razón.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTA PANTALLA TIENE QUE SOSTENER
 *
 * 1. **Que la pregunta sea la tarea, no la luz.** El cliente pide «más luz» y lo que le
 *    falta es luz *donde hace la cosa*. Por eso cada espacio muestra en qué superficie está
 *    la tarea, no sólo cuántos lux quiere.
 *
 * 2. **Que nunca se diga «cumple norma» donde no hay norma.** El régimen —exigible o
 *    recomendado— se marca por espacio y sale escrito en el resultado. Es la diferencia
 *    entre un argumento de venta y una promesa que no se puede sostener.
 *
 * 3. **Que el módulo de control se cuente por espacio.** Un cuarto con doce luminarias
 *    atenuadas lleva UN módulo. Contarlos por lámpara multiplica el presupuesto por diez y
 *    es el error que más caro sale en este rubro.
 */

import { useState, useMemo } from 'react'
import PasoGuia, { Avance } from './PasoGuia.jsx'
import Materiales from './Materiales.jsx'
import Sello from './Sello.jsx'
import { GUIA_ILUMINACION } from '../motor/guia.js'
import {
  ESPACIOS, espacioPorId, CONTROL, controlPorId, NOM025, ALCANCE_NOM025,
  calcular, FACTOR_MANTENIMIENTO, UTILIZACION
} from '../motor/iluminacion.js'

const nuevoId = () => Math.random().toString(36).slice(2, 8)

export default function Iluminacion ({ atras, onGuardar, insumos = [], preferencias = {} }) {
  const [i, setI] = useState(0)
  const [espacios, setEspacios] = useState([])
  const [abierto, setAbierto] = useState(null)
  const [lumenes, setLumenes] = useState('800')
  const [notas, setNotas] = useState('')

  const paso = GUIA_ILUMINACION.pasos[i]

  const r = useMemo(() => {
    const listos = espacios.filter(e => e.largo > 0 && e.ancho > 0)
    if (!listos.length) return null
    return calcular({
      espacios: listos.map(e => ({ ...e, lumenes: parseFloat(lumenes) || 800 })),
      insumos
    })
  }, [espacios, lumenes, insumos])

  const agregar = () => {
    const id = nuevoId()
    setEspacios([...espacios, {
      id, espacioId: 'sala', largo: 0, ancho: 0, alturaMontaje: 2.3,
      control: 'sencillo', esCentroDeTrabajo: false
    }])
    setAbierto(id)
  }
  const cambiar = (id, cambios) =>
    setEspacios(espacios.map(e => e.id === id ? { ...e, ...cambios } : e))
  const quitar = id => setEspacios(espacios.filter(e => e.id !== id))

  const falta = (() => {
    if (paso.id === 'medir' && !espacios.some(e => e.largo > 0 && e.ancho > 0)) {
      return 'Agrega al menos un espacio con sus medidas.'
    }
    return null
  })()

  return (
    <div className="app">
      <div className="bar">
        <button className="ib ico" onClick={atras} aria-label="Regresar">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <h1>Iluminación
          <span className="crumb">{GUIA_ILUMINACION.duracion} · paso {i + 1} de {GUIA_ILUMINACION.pasos.length}</span>
        </h1>
      </div>

      <main>
        <Avance pasos={GUIA_ILUMINACION.pasos} i={i} onIr={setI} />
        <PasoGuia key={paso.id} paso={paso} />

        {paso.id === 'regimen' && (
          <div className="captura">
            {/* La tabla de la norma, a la mano. No es relleno: es lo que permite decirle a
                un cliente comercial el número exacto que le exige la ley, en el momento. */}
            <div className="nota leible">
              <b>Lo que exige la NOM-025-STPS-2008</b>, sólo si el espacio es centro de trabajo:
              <ul style={{ margin: 'var(--e3) 0 0', paddingLeft: '1.1rem' }}>
                {NOM025.filter(n => [100, 300, 500, 750].includes(n.lux)).map(n => (
                  <li key={n.lux}><b>{n.lux} lux</b> — {n.area}</li>
                ))}
              </ul>
              <p style={{ margin: 'var(--e3) 0 0' }}>{ALCANCE_NOM025}</p>
            </div>
          </div>
        )}

        {paso.id === 'tarea' && (
          <div className="captura">
            <div className="nota leible">
              <b>Dónde está la tarea en cada espacio.</b> Es lo que hay que iluminar, y casi
              nunca es todo el cuarto:
              <ul style={{ margin: 'var(--e3) 0 0', paddingLeft: '1.1rem' }}>
                {ESPACIOS.slice(0, 6).map(e => (
                  <li key={e.id}><b>{e.nombre}:</b> {e.donde}</li>
                ))}
              </ul>
            </div>
            <label className="f" style={{ marginTop: 'var(--e4)', marginBottom: 0 }}>
              <span>Lo que observaste y lo que pidió el cliente</span>
              <textarea rows="3" value={notas} onChange={e => setNotas(e.target.value)}
                placeholder="Ej. La barra de la cocina queda en sombra con el plafón actual. Quiere poder atenuar la sala para ver películas. El pasillo se queda prendido toda la noche." />
            </label>
          </div>
        )}

        {(paso.id === 'medir' || paso.id === 'control') && (
          <div className="captura">
            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>
              Espacios ({espacios.length})
            </div>

            {espacios.map(e => {
              const def = espacioPorId(e.espacioId)
              const esteAbierto = abierto === e.id
              return (
                <div className="card" key={e.id}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <button type="button" className="grow" style={{
                      background: 'none', border: 0, textAlign: 'left', cursor: 'pointer', padding: 0
                    }} onClick={() => setAbierto(esteAbierto ? null : e.id)}>
                      <div className="t">{def.nombre}</div>
                      <div className="s">
                        {e.largo > 0 && e.ancho > 0
                          ? `${e.largo} × ${e.ancho} m · ${(e.largo * e.ancho).toFixed(1)} m²`
                          : 'Sin medir'}
                        {' · '}{controlPorId(e.control).nombre.toLowerCase()}
                        {e.esCentroDeTrabajo && ' · centro de trabajo'}
                      </div>
                    </button>
                    <button className="ib sm dz" aria-label={`Quitar ${def.nombre}`}
                      onClick={() => quitar(e.id)}>×</button>
                  </div>

                  {esteAbierto && (
                    <div style={{ marginTop: 'var(--e4)' }}>
                      <label className="f">
                        <span>Qué espacio es</span>
                        <select value={e.espacioId}
                          onChange={ev => cambiar(e.id, { espacioId: ev.target.value })}>
                          {ESPACIOS.map(x => (
                            <option key={x.id} value={x.id}>
                              {x.nombre} — {x.general} lux
                            </option>
                          ))}
                        </select>
                        <span style={{ marginTop: 4 }}>{def.donde}</span>
                      </label>

                      <div className="two">
                        <label className="f">
                          <span>Largo, en metros</span>
                          <input type="number" inputMode="decimal" step="0.1" value={e.largo || ''}
                            onChange={ev => cambiar(e.id, { largo: parseFloat(ev.target.value) || 0 })} />
                        </label>
                        <label className="f">
                          <span>Ancho, en metros</span>
                          <input type="number" inputMode="decimal" step="0.1" value={e.ancho || ''}
                            onChange={ev => cambiar(e.id, { ancho: parseFloat(ev.target.value) || 0 })} />
                        </label>
                      </div>

                      <label className="f">
                        <span>Altura del techo al plano de trabajo, en metros</span>
                        <input type="number" inputMode="decimal" step="0.1" value={e.alturaMontaje}
                          onChange={ev => cambiar(e.id, { alturaMontaje: parseFloat(ev.target.value) || 2.3 })} />
                        <span>Del techo a donde se hace la tarea. Barra 0.90 m del piso,
                              escritorio 0.75, piso 0. Con techo de 2.60 y barra, son 1.70.</span>
                      </label>

                      <div className="sec" style={{ margin: 'var(--e4) 0 var(--e3)' }}>Cómo se controla</div>
                      <div className="opciones">
                        {CONTROL.map(c => (
                          <button key={c.id} type="button"
                            className={`opcion ${e.control === c.id ? 'on' : ''}`}
                            onClick={() => cambiar(e.id, { control: c.id })}>
                            <b>{c.nombre}</b>
                            <span className="s">{c.que}</span>
                            <span className="s">{c.para}</span>
                          </button>
                        ))}
                      </div>

                      {/* La bandera que cambia el régimen legal. Va con su consecuencia
                          escrita al lado, no como una casilla suelta. */}
                      <label className="f" style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        margin: 'var(--e4) 0 0'
                      }}>
                        <input type="checkbox" checked={!!e.esCentroDeTrabajo}
                          style={{ width: 20, height: 20, marginTop: 2 }}
                          onChange={ev => cambiar(e.id, { esCentroDeTrabajo: ev.target.checked })} />
                        <span style={{ margin: 0 }}>
                          Aquí trabaja alguien contratado
                          <span className="s" style={{ display: 'block' }}>
                            Si lo marcas, el nivel deja de ser recomendación y pasa a ser
                            exigible por la NOM-025. Cambia lo que se le puede prometer al
                            cliente, no el cálculo.
                          </span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )
            })}

            <button className="ib pri w" onClick={agregar} style={{ marginTop: 'var(--e3)' }}>
              Agregar espacio
            </button>
          </div>
        )}

        {paso.id === 'calcular' && (
          <>
            <div className="captura">
              <label className="f" style={{ marginBottom: 0 }}>
                <span>Lúmenes de la luminaria que vas a usar</span>
                <input type="number" inputMode="numeric" value={lumenes}
                  onChange={e => setLumenes(e.target.value)} />
                <span>Viene impreso en la caja. Es el dato que más mueve el resultado:
                      menos luminarias más potentes suele ser mejor instalación y más
                      barata que muchas chicas.</span>
              </label>
            </div>
            {r ? <Resultado r={r} notas={notas} onGuardar={onGuardar} />
               : <div className="empty">
                   <b>Todavía no se puede calcular.</b>
                   <p>Agrega al menos un espacio con su largo y su ancho.</p>
                 </div>}
          </>
        )}
      </main>

      <div className="fab"><div className="in">
        <button className="gh" onClick={() => setI(Math.max(i - 1, 0))} disabled={i === 0}
          aria-label="Paso anterior">←</button>
        <button onClick={() => setI(Math.min(i + 1, GUIA_ILUMINACION.pasos.length - 1))}
          disabled={!!falta || i === GUIA_ILUMINACION.pasos.length - 1}>
          {falta || (i === GUIA_ILUMINACION.pasos.length - 1 ? 'Listo' : 'Siguiente')}
        </button>
      </div></div>
    </div>
  )
}

function Resultado ({ r, notas, onGuardar }) {
  const [auditando, setAuditando] = useState(false)

  return (
    <>
      <div className="sec">Espacio por espacio</div>
      <table className="d">
        <thead>
          <tr>
            <th>Espacio</th>
            <th className="n">m²</th>
            <th className="n">Lux</th>
            <th className="n">Luminarias</th>
          </tr>
        </thead>
        <tbody>
          {r.detalle.map((d, n) => (
            <tr key={n}>
              <td>
                {d.espacio.nombre}
                {/* El régimen va en cada renglón, no en una nota al pie: es lo que decide
                    qué se le puede decir al cliente sobre ESE espacio. */}
                <div className="s">
                  {d.regimen === 'norma'
                    ? `Exigible por norma · mínimo ${d.exigible?.lux} lux`
                    : 'Recomendado, no exigible'}
                </div>
                {auditando && <div className="s">{d.luminarias.porque}</div>}
                {auditando && <div className="s">{d.utilizacion.porque}</div>}
              </td>
              <td className="n">{d.areaM2.toFixed(1)}</td>
              <td className="n">
                {Math.round(d.luminarias.luxReales)}
                {auditando && <div className="s">objetivo {d.lux}</div>}
              </td>
              <td className="n">{d.luminarias.cantidad}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="3"><b>Total</b></td>
            <td className="n"><b>{r.totalLuminarias}</b></td>
          </tr>
        </tfoot>
      </table>

      {/* La distinción de régimen, dicha una vez y completa. Si hay espacios de los dos
          tipos en la misma cotización, hay que separarlos al hablar con el cliente. */}
      {r.espaciosConNorma > 0 ? (
        <div className="nota leible">
          <b>{r.espaciosConNorma} de {r.detalle.length} espacios son centro de trabajo.</b>{' '}
          Ahí el nivel lo exige la NOM-025-STPS-2008 y una inspección lo puede medir con
          luxómetro. En los demás es una recomendación de confort. <b>No mezcles las dos
          cosas al presentarlo:</b> prometer «cumplimiento de norma» en un espacio que no lo
          requiere es prometer algo que no existe.
        </div>
      ) : (
        <div className="nota leible">
          <b>Ningún espacio es centro de trabajo</b>, así que estos niveles son
          recomendación de confort, no obligación legal. No hay norma de iluminación para
          vivienda —ni en México ni en Europa—, y decir que «cumple norma» sería inexacto.
          Lo que sí se puede decir es que está diseñado con criterio y que se puede medir.
        </div>
      )}

      {/* La advertencia de atenuación, una sola vez y sólo si aplica. */}
      {r.detalle.some(d => d.avisoAtenuacion) && (
        <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
          <b>Ojo con la atenuación.</b> {r.detalle.find(d => d.avisoAtenuacion).avisoAtenuacion}
        </div>
      )}

      <div className="sec">Control</div>
      {Object.entries(r.porControl).length ? Object.entries(r.porControl).map(([id, n]) => (
        <div className="ln" key={id}>
          <span>{controlPorId(id).nombre}
            <span className="s" style={{ display: 'block' }}>{controlPorId(id).que}</span>
          </span>
          <b>{n}</b>
        </div>
      )) : (
        <div className="nota leible">
          Todos los espacios llevan control convencional: no hay módulos que cotizar.
        </div>
      )}
      <div className="nota leible">
        Los módulos se cuentan <b>por espacio, no por lámpara</b>. Un cuarto con doce
        luminarias atenuadas lleva un módulo, no doce: el módulo maneja todo el circuito.
      </div>

      <div className="sec">Material</div>
      <Materiales lista={r.materiales} auditando={auditando} />

      <button type="button" className="discreto" style={{ marginTop: 'var(--e5)' }}
        onClick={() => setAuditando(!auditando)} aria-expanded={auditando}>
        {auditando ? 'Ocultar de dónde sale cada número' : 'Ver de dónde sale cada número'}
      </button>

      {auditando && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <p style={{ marginTop: 0 }}>
            <b>Método de lúmenes.</b> Luminarias = (lux × área) ÷ (lúmenes × utilización ×
            mantenimiento). Se redondea hacia arriba: media luminaria no existe, y quedarse
            abajo del nivel es el defecto que el cliente sí nota.
          </p>
          <p>
            <b>Factor de mantenimiento {FACTOR_MANTENIMIENTO.valor}.</b> Una luminaria
            entrega su flujo nominal el día que se instala y menos cada mes. Diseñar sin
            este factor garantiza estar por debajo del nivel en un año.
          </p>
          <p>
            <b>Coeficiente de utilización {UTILIZACION.valor}.</b> Es un <b>valor de
            trabajo</b>, no de tabla. El exacto sale de la tabla fotométrica de la luminaria
            que se elija, cruzando el índice del local con las reflectancias del cuarto.
            Por eso el índice se calcula y se muestra: para poder corregirlo. <Sello />
          </p>
          <p style={{ marginBottom: 0 }}>
            <b>Niveles.</b> Los de centro de trabajo salen de la Tabla 1 de la
            NOM-025-STPS-2008, leída del DOF. Los de vivienda son recomendación de la
            práctica, porque no existe norma que los exija.
          </p>
        </div>
      )}

      {notas && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <b>Del recorrido:</b> {notas}
        </div>
      )}

      {onGuardar && (
        <button className="ib pri w" style={{ marginTop: 'var(--e6)' }}
          onClick={() => onGuardar(r)}>Agregar a la cotización</button>
      )}
    </>
  )
}
